import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import GlitterWarp from '../components/GlitterWarp';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    setLoading(true);
    try {
      const r = await login(email.trim(), password);
      if (r && r.token) {
        toast.success('¡Bienvenido Tripulante!');
        if (email.trim().toLowerCase() === 'contabilidadzoikagency@gmail.com') {
          navigate('/team');
        } else {
          navigate('/dashboard');
        }
      } else {
        toast.error((r && r.error) ? r.error : 'Credenciales inválidas');
      }
    } catch (err: any) {
      const msg = err && err.response && err.response.data && err.response.data.error;
      toast.error(msg || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      background: '#030712',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      fontFamily: '"Montserrat", sans-serif',
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      {/* Animación de entrada y estilos Glassmorphism */}
      <style>{`
        @keyframes cardEntrance {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(30px);
            filter: blur(12px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
            filter: blur(0px);
          }
        }

        .glass-card {
          width: 100%;
          max-width: 420px;
          background: rgba(10, 25, 47, 0.55);
          backdrop-filter: blur(36px);
          -webkit-backdrop-filter: blur(36px);
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.4),
            0 0 40px rgba(0, 112, 243, 0.18),
            inset 0 1px 0 rgba(255, 255, 255, 0.5),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            inset 0 0 34px 17px rgba(0, 112, 243, 0.15);
          position: relative;
          overflow: hidden;
          padding: 46px 36px;
          box-sizing: border-box;
          z-index: 10;
          text-align: center;
          animation: cardEntrance 0.9s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          will-change: transform, opacity, filter;
        }

        @media (max-width: 480px) {
          .glass-card {
            padding: 30px 20px !important;
            border-radius: 20px !important;
          }
        }

        .glass-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.8),
            transparent
          );
          pointer-events: none;
        }

        .glass-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 1px;
          height: 100%;
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.8),
            transparent,
            rgba(255, 255, 255, 0.3)
          );
          pointer-events: none;
        }

        /* Limpieza de autocompletado */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #0b1c33 inset !important;
          -webkit-text-fill-color: #ffffff !important;
          transition: background-color 5000s ease-in-out 0s !important;
          caret-color: #ffffff !important;
        }
        input::placeholder {
          color: rgba(255, 255, 255, 0.45) !important;
        }
        input {
          background-color: transparent !important;
          box-shadow: none !important;
        }
      `}</style>

      {/* Fondo Animado Glitter Warp */}
      <GlitterWarp
        particleCount={450}
        speed={1.1}
        starColor="#ffffff"
        glowColor="#0070f3"
      />

      {/* Capa de oscurecimiento sutil del fondo */}
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'radial-gradient(circle at center, rgba(3, 7, 18, 0.35) 0%, rgba(2, 6, 14, 0.7) 100%)',
        pointerEvents: 'none',
        zIndex: 1
      }} />

      {/* Tarjeta Glassmorphism con Animación de Entrada */}
      <div className="glass-card">
        {/* Logo Centrado */}
        <div style={{ marginBottom: '18px' }}>
          <img
            src="/logo.png"
            alt="ZOIK Analyst"
            style={{
              height: '36px',
              maxWidth: '75%',
              objectFit: 'contain',
              display: 'inline-block'
            }}
          />
        </div>

        {/* Título en Regular */}
        <h2 style={{
          margin: '0 0 30px',
          fontSize: '20px',
          fontWeight: 400,
          color: '#ffffff',
          letterSpacing: '0.2px'
        }}>
          ¡Bienvenido Tripulante!
        </h2>

        {/* Formulario */}
        <form onSubmit={handleCredentials} autoComplete="on">
          {/* Input Email */}
          <div style={{
            marginBottom: '20px',
            background: '#0b1c33',
            borderRadius: '50px',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            display: 'flex',
            alignItems: 'center',
            padding: '5px',
            boxSizing: 'border-box',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            {/* Círculo Blanco con Ícono de Usuario */}
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              background: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a192f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Username / Email"
              required
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#ffffff',
                fontSize: '14px',
                padding: '0 16px',
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 500,
                width: '100%'
              }}
            />
          </div>

          {/* Input Password */}
          <div style={{
            marginBottom: '28px',
            background: '#0b1c33',
            borderRadius: '50px',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            display: 'flex',
            alignItems: 'center',
            padding: '5px',
            boxSizing: 'border-box',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password"
              required
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#ffffff',
                fontSize: '14px',
                padding: '0 12px 0 20px',
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 500,
                width: '100%'
              }}
            />
            {/* Botón Ojo Blanco */}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                opacity: 0.85
              }}
              title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>

            {/* Círculo Blanco con Ícono de Candado */}
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '50%',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a192f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
          </div>

          {/* Botón ABORDAR */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              height: '50px',
              borderRadius: '50px',
              border: 'none',
              background: '#ffffff',
              color: '#0a192f',
              fontWeight: 900,
              fontSize: '15px',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: '"Montserrat", sans-serif',
              opacity: loading ? 0.75 : 1,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.45)',
              transition: 'all 0.2s ease'
            }}
          >
            {loading ? 'ABORDANDO...' : 'ABORDAR'}
          </button>

          {/* Texto: ¿Olvidaste tu contraseña? */}
          <div style={{ marginTop: '22px' }}>
            <button
              type="button"
              onClick={() => setShowForgotModal(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: '"Montserrat", sans-serif',
                textDecoration: 'none',
                padding: '4px',
                opacity: 0.85
              }}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '28px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.45)',
        textAlign: 'center',
        userSelect: 'none',
        zIndex: 10,
        letterSpacing: '0.2px'
      }}>
        Zoik Analyst By Zoik Agency 2026 todos los derechos reservados.
      </div>

      {/* Modal Olvidé mi Contraseña */}
      {showForgotModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'rgba(15, 30, 55, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            backdropFilter: 'blur(25px)',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '380px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 50px rgba(0,0,0,0.7)'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '14px' }}>🔐</div>
            <h3 style={{ margin: '0 0 10px', fontSize: '18px', color: 'white', fontWeight: 800 }}>
              Restablecer Contraseña
            </h3>
            <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, marginBottom: '24px' }}>
              Para restablecer tu acceso, por favor contacta al administrador de la plataforma.
            </p>
            <button
              onClick={() => setShowForgotModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '50px',
                border: 'none',
                background: '#0070f3',
                color: 'white',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                fontFamily: '"Montserrat", sans-serif'
              }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
